// src/reports/StoreBriefingReport.jsx — Reporting → Store Reports → Store
// Briefing (Eric, 2026-09-30). A nightly briefing per corporate store, written
// for the store manager: a short brief and a to-do list at the top, then the
// numbers behind it. The backend (store_briefing.py) builds it once a night:
// fixed queries produce every number; Claude writes the brief and the to-do
// list from those numbers. This page only reads the stored result.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Card, BlockStack, InlineStack, InlineGrid, Text, Select, Banner, Spinner, Link, EmptyState, DataTable, List, Divider, Box,
} from '@shopify/polaris';
import { useSearchParams } from 'react-router-dom';
import { useAuthFetch } from '../AuthComponent';
import { ReportPicker } from '../Reporting';

const fmtN = n => (n === null || n === undefined || Number.isNaN(Number(n))) ? '—' : Math.round(Number(n)).toLocaleString();
const fmtUSD = n => (n === null || n === undefined || Number.isNaN(Number(n))) ? '—' : '$' + Math.round(Number(n)).toLocaleString();
const fmtSigned = n => (n === null || n === undefined || Number.isNaN(Number(n))) ? '—' : `${Number(n) < 0 ? '-' : '+'}$${Math.abs(Math.round(Number(n))).toLocaleString()}`;
const fmtPct = n => (n === null || n === undefined) ? '—' : `${n > 0 ? '+' : ''}${Math.round(n)}%`;
const pctChange = (a, b) => (b ? (a / b - 1) * 100 : null);
// "(ADID-JL8540) Adidas Mexico …" → the full product name without the leading SKU (shown on its own line).
const itemName = s => { const t = String(s || ''); return t.startsWith('(') && t.includes(') ') ? t.slice(t.indexOf(') ') + 2) : t; };
const styleSku = s => { const t = String(s || ''); return t.startsWith('(') && t.includes(')') ? t.slice(1, t.indexOf(')')) : ''; };
function fmtDay(iso) {
    if (!iso) return '';
    return new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}
// The day a briefing was built, in store time: "Monday, October 12, 2026". A briefing is
// filed under the last sales day it covers (as_of), but people read it the morning after,
// so the label is the build day (Eric, 2026-10-12).
function fmtBuildDay(iso, fallbackAsOf) {
    if (!iso) return fallbackAsOf ? fmtDay(fallbackAsOf) : '';
    return new Date(iso).toLocaleDateString('en-US', { timeZone: 'America/New_York', weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}
function fmtWhen(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return `${d.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' })} ${d.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' })} ET`;
}

// House tile style (same as Store Velocity).
function Tile({ label, value, sub, tone }) {
    const bg = { ok: '#e3f3ea', info: '#e6edfb', warn: '#fdebd3', bad: '#fde2e1', muted: '#f1f2f4' }[tone || 'muted'];
    return (
        <div style={{ background: bg, borderRadius: 8, padding: '10px 14px', minWidth: 0, boxSizing: 'border-box' }}>
            <div style={{ fontSize: 24, fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1.15 }}>{value}</div>
            <div style={{ fontSize: 12, color: '#6d7175', textTransform: 'uppercase', letterSpacing: '.04em', marginTop: 2 }}>{label}</div>
            {sub ? <div style={{ fontSize: 12, color: '#6d7175', marginTop: 4 }}>{sub}</div> : null}
        </div>
    );
}

// Product cell: full name (never cut off) with the SKU underneath.
// When B2B lists the product, the name links to its page on the B2B site (Eric, 2026-09-30).
const B2B_PRODUCT_URL = 'https://soccerpostb2b.com/products/';
// Team Notes: plain text from Settings. Blank lines separate paragraphs; lines starting with "-" or "•" are bullets.
function TeamNotes({ text }) {
    const blocks = String(text || '').split(/\n\s*\n/).map(b => b.trim()).filter(Boolean);
    return (
        <BlockStack gap="200">
            {blocks.map((b, i) => {
                const lines = b.split('\n').map(l => l.trim()).filter(Boolean);
                const bullets = lines.length > 0 && lines.every(l => /^[-•*]\s+/.test(l));
                return bullets
                    ? <List key={i}>{lines.map((l, k) => <List.Item key={k}>{l.replace(/^[-•*]\s+/, '')}</List.Item>)}</List>
                    : <Text as="p" key={i}>{lines.join(' ')}</Text>;
            })}
        </BlockStack>
    );
}

function Item({ name, sku, note, handle }) {
    return (
        <div style={{ whiteSpace: 'normal', minWidth: 150 }}>
            <div>{handle
                ? <Link url={`${B2B_PRODUCT_URL}${encodeURIComponent(handle)}`} target="_blank" removeUnderline>{itemName(name)}</Link>
                : itemName(name)}</div>
            {(sku || note) ? <div style={{ fontSize: 12, color: '#6d7175' }}>{[sku, note].filter(Boolean).join(' · ')}</div> : null}
        </div>
    );
}

// Text written by the AI, with the product names it flagged turned into B2B links.
// `links` = [{ text, handle }], each `text` an exact phrase in the string (checked by the backend).
function Linked({ text, links }) {
    const str = String(text || '');
    const marks = [];
    (links || []).forEach(l => {
        let from = 0;
        for (;;) {
            const i = str.indexOf(l.text, from);
            if (i < 0) break;
            if (!marks.some(m => i < m.end && i + l.text.length > m.start)) marks.push({ start: i, end: i + l.text.length, handle: l.handle });
            from = i + l.text.length;
        }
    });
    if (!marks.length) return str;
    marks.sort((a, b) => a.start - b.start);
    const out = []; let pos = 0;
    marks.forEach((m, k) => {
        if (m.start > pos) out.push(str.slice(pos, m.start));
        out.push(<Link key={k} url={`${B2B_PRODUCT_URL}${encodeURIComponent(m.handle)}`} target="_blank">{str.slice(m.start, m.end)}</Link>);
        pos = m.end;
    });
    if (pos < str.length) out.push(str.slice(pos));
    return <>{out}</>;
}

// An action line from the AI: **bold** marks the part the manager must not miss, and product
// names become B2B links. Bold is parsed first, then each piece is linked.
function Rich({ text, links }) {
    const parts = String(text || '').split('**');
    return <>{parts.map((part, i) => (i % 2 === 1
        ? <Text as="span" fontWeight="bold" key={i}><Linked text={part} links={links} /></Text>
        : <React.Fragment key={i}><Linked text={part} links={links} /></React.Fragment>))}</>;
}

function Table({ headings, types, rows }) {
    if (!rows.length) return <Text as="p" tone="subdued">Nothing to show.</Text>;
    // DataTable needs its own scroll container or it widens the page on narrow screens.
    return <div style={{ overflowX: 'auto' }}><DataTable columnContentTypes={types} headings={headings} rows={rows} hideScrollIndicator /></div>;
}

export default function StoreBriefingReport() {
    const fetchAuth = useAuthFetch();

    const [locations, setLocations] = useState([]);
    const [locLoaded, setLocLoaded] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [locationId, setLocationId] = useState(searchParams.get('location_id') || '');
    const [date, setDate] = useState(searchParams.get('date') || '');
    const [report, setReport] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        const n = new URLSearchParams();
        if (locationId) n.set('location_id', locationId);
        if (date) n.set('date', date);
        if (n.toString() !== searchParams.toString()) setSearchParams(n, { replace: true });
    }, [locationId, date]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await fetchAuth('/api/reports/store_briefing/locations');
                const j = await r.json();
                if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
                if (cancelled) return;
                const locs = j.locations || [];
                setLocations(locs);
                // Open on a store straight away (Sarah Jett, 2026-10-05): the one used last time, else the first.
                // The newest briefing (sales through yesterday) is what loads by default.
                if (locs.length) {
                    setLocationId(prev => {
                        if (prev && locs.some(l => String(l.id) === String(prev))) return prev;
                        let last = null;
                        try { last = localStorage.getItem('store_briefing_last_store'); } catch (e) { /* storage blocked */ }
                        const pick = (last && locs.some(l => String(l.id) === last)) ? last : String(locs[0].id);
                        return pick;
                    });
                }
            } catch (e) { if (!cancelled) setErr(e.message); }
            finally { if (!cancelled) setLocLoaded(true); }
        })();
        return () => { cancelled = true; };
    }, [fetchAuth]);

    const load = useCallback(async () => {
        if (!locationId) { setReport(null); return; }
        setBusy(true); setErr(null);
        try {
            const p = new URLSearchParams({ location_id: locationId });
            if (date) p.set('date', date);
            const r = await fetchAuth(`/api/reports/store_briefing?${p}`);
            const j = await r.json();
            if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
            setReport(j);
        } catch (e) { setErr(e.message); setReport(null); }
        finally { setBusy(false); }
    }, [fetchAuth, locationId, date]);
    useEffect(() => { load(); }, [load]);

    const locationOptions = useMemo(() => locations.map(l => ({ value: String(l.id), label: l.name })), [locations]);

    if (locLoaded && !locations.length && !err) {
        return (
            <Card>
                <EmptyState heading="No Lightspeed store is mapped to your account" image="">
                    <p>Ask an admin to map it under Settings → Lightspeed.</p>
                </EmptyState>
            </Card>
        );
    }

    const b = report?.briefing;
    const d = b?.data;
    const n = b?.narrative;
    const nearby = d?.peer_scope === 'all' ? 'the other Soccer Post stores' : 'nearby stores';
    const Nearby = d?.peer_scope === 'all' ? 'Other Soccer Post stores' : 'Nearby stores';

    return (
        <BlockStack gap="400">
            <Card>
                <BlockStack gap="200">
                    <Text as="h2" variant="headingMd">Store Briefing</Text>
                    <Text as="p" tone="subdued">
                        What to do this week and the numbers behind it, rebuilt every night for each store.
                    </Text>
                    {/* Where the numbers come from sits at the top, with the AI note in bold (Eric, 2026-09-30). */}
                    {b && d && (
                        <Text as="p" tone="subdued">
                            {/* Sales calendar (Eric, 2026-10-06): whole Monday–Sunday weeks and 4-4-5 periods, as in the Stores Sales sheet. */}
                            Sales are from Lightspeed through {fmtDay(d.period?.through || b.as_of)}, after discounts and returns and before tax,
                            in whole weeks (Monday to Sunday) on the company sales calendar.{d.period ? ` "${d.period.name}" is the ${d.period.weeks}-week period
                            ${fmtDay(d.period.start)} – ${fmtDay(d.period.end)}; this briefing covers its first ${d.period.week === d.period.weeks ? `${d.period.weeks} weeks, the whole period` : `${d.period.week} week${d.period.week > 1 ? 's' : ''}`}.` : ''} Last year means the same weekdays a
                            year earlier. {d.peer_scope === 'all'
                                ? 'Your region has too few stores to compare, so the comparison is every other Soccer Post store.'
                                : `Nearby stores are ${(d.peers || []).map(p => p.name).join(', ')}.`} B2B stock is from last night. Pickup orders are
                            from the Operations Hub. <Text as="span" fontWeight="bold" tone="base">The numbers are calculated; the written guidance is
                            drafted by AI from those numbers.</Text>
                        </Text>
                    )}
                    {err && <Banner tone="critical" onDismiss={() => setErr(null)}>{err}</Banner>}
                    <InlineStack gap="300" blockAlign="end" wrap>
                        <ReportPicker label="Store" options={locationOptions} value={locationId} onChange={v => {
                            setDate(''); setLocationId(v);
                            try { localStorage.setItem('store_briefing_last_store', String(v)); } catch (e) { /* storage blocked */ }
                        }} />
                        {(report?.dates || []).length > 1 && (
                            <Select label="Briefing for" labelInline
                                options={(report.builds || report.dates.map(x => ({ as_of: x }))).map(x => ({ value: x.as_of, label: fmtBuildDay(x.generated_at, x.as_of) }))}
                                value={b?.as_of || report.dates[0]} onChange={setDate} />
                        )}
                    </InlineStack>
                    {b && (
                        <BlockStack gap="050">
                            <Text as="h3" variant="headingSm">Briefing for {fmtBuildDay(b.generated_at, b.as_of)}</Text>
                            <Text as="span" tone="subdued">
                                Sales through {fmtDay(b.data?.period?.through || b.as_of)} · built {fmtWhen(b.generated_at)}
                            </Text>
                        </BlockStack>
                    )}
                </BlockStack>
            </Card>

            {!locationId ? (
                <Card><Text as="p" tone="subdued">Pick a store to see its briefing.</Text></Card>
            ) : busy && !report ? (
                <Card><div style={{ textAlign: 'center', padding: 40 }}><Spinner accessibilityLabel="Loading Store Briefing" size="large" /></div></Card>
            ) : !b ? (
                <Card><Text as="p" tone="subdued">No briefing has been built for this store yet. Briefings are built every night.</Text></Card>
            ) : (
                <>
                    <Card>
                        <BlockStack gap="300">
                            <Text as="h2" variant="headingLg">{d.town}</Text>
                            {n?.brief
                                ? <Text as="p" variant="bodyLg">{n.brief}</Text>
                                : <Banner tone="warning">
                                    The written guidance is not available for this day. The numbers below are complete.
                                    {b.error ? ` (${b.error})` : ''}
                                </Banner>}
                            <InlineGrid columns={{ xs: 2, md: d.goal?.has ? 5 : 4 }} gap="300">
                                <Tile label="Last week" value={fmtUSD(d.pulse.me.d7.sales)}
                                    sub={[`${fmtDay(d.windows.d7[0])} – ${fmtDay(d.windows.d7[1])}`,
                                        d.pulse.me.ly7?.sales > 0 ? `${fmtUSD(d.pulse.me.ly7.sales)} last year (${fmtPct(d.trend.pct_7_vs_ly)})` : null,
                                        d.goal?.d7 ? `goal ${fmtUSD(d.goal.d7)} (${fmtPct(d.trend.pct_7_vs_goal)})` : null].filter(Boolean).join(' · ')} />
                                <Tile label={d.period ? `${d.period.name}, week ${d.period.week} of ${d.period.weeks}` : 'This period'} value={fmtUSD(d.pulse.me.d30.sales)}
                                    sub={`${fmtDay(d.windows.d30[0])} – ${fmtDay(d.windows.d30[1])} · ${fmtUSD(d.pulse.me.d30.sales / (d.period?.days || 30))} a day`} />
                                {/* Real dollars, not just a percentage (Eric, 2026-09-30): the change in dollars up top,
                                    this year against last year and the percentage underneath. */}
                                <Tile label="Compared with last year"
                                    value={d.pulse.me.ly30.sales > 0 ? fmtSigned(d.pulse.me.d30.sales - d.pulse.me.ly30.sales) : '—'}
                                    sub={d.pulse.me.ly30.sales > 0
                                        ? `${fmtUSD(d.pulse.me.d30.sales)} vs ${fmtUSD(d.pulse.me.ly30.sales)} in the same period last year (${fmtPct(d.trend.pct_30_vs_ly)})`
                                        : 'No sales in the same period last year'}
                                    tone={d.pulse.me.ly30.sales > 0 ? (d.pulse.me.d30.sales >= d.pulse.me.ly30.sales ? 'ok' : 'warn') : 'muted'} />
                                {/* Against goal (Eric, 2026-10-05): the daily goals from the Goals sheet, summed over the same period to date. */}
                                {d.goal?.has && (
                                    <Tile label="Compared with goal"
                                        value={d.goal.d30 ? fmtSigned(d.pulse.me.d30.sales - d.goal.d30) : '—'}
                                        sub={d.goal.d30
                                            ? `${fmtUSD(d.pulse.me.d30.sales)} vs a ${fmtUSD(d.goal.d30)} goal for the period so far (${fmtPct(d.trend.pct_30_vs_goal)})`
                                            : 'No goal set for this period'}
                                        tone={d.goal.d30 ? (d.pulse.me.d30.sales >= d.goal.d30 ? 'ok' : 'warn') : 'muted'} />
                                )}
                                <Tile label="Last week's pace"
                                    value={{ 'picking up': 'Picking up', slowing: 'Slowing', steady: 'Steady' }[d.trend.label] || '—'}
                                    sub={`${fmtPct(d.trend.pct_7_vs_30)} vs your pace this period`}
                                    tone={{ 'picking up': 'ok', slowing: 'bad', steady: 'info' }[d.trend.label]} />
                            </InlineGrid>
                        </BlockStack>
                    </Card>

                    {/* Team Notes (Eric, 2026-10-05): what every store needs to know and do this week, written by the
                        regional team in Settings → Briefing. Shown on every store's briefing. */}
                    {String(report?.team_notes || '').trim() && (
                        <Card background="bg-surface-info">
                            <BlockStack gap="200">
                                <InlineStack gap="200" blockAlign="baseline">
                                    <Text as="h3" variant="headingMd">Team Notes</Text>
                                    {report.team_notes_updated_at && <Text as="span" tone="subdued" variant="bodySm">updated {fmtWhen(report.team_notes_updated_at)}</Text>}
                                </InlineStack>
                                <TeamNotes text={report.team_notes} />
                            </BlockStack>
                        </Card>
                    )}

                    <Card>
                        <BlockStack gap="300">
                            {/* Right below the Sales banner (Sarah Jett, 2026-10-05): orders at the store that nobody has marked
                                ready are the thing a manager must not miss. */}
                            <Text as="h3" variant="headingMd">Team Portal pickups</Text>
                            {n?.pickups && <Text as="p">{n.pickups}</Text>}
                            <InlineGrid columns={{ xs: 1, md: 3 }} gap="300">
                                <Tile label="On the way" value={fmtN(d.pickups.on_the_way)} sub="still being made" />
                                <Tile label="At the store, not marked ready" value={fmtN(d.pickups.at_store_not_marked_ready)} sub="mark these ready"
                                    tone={d.pickups.at_store_not_marked_ready > 0 ? 'warn' : 'muted'} />
                                <Tile label="Ready, waiting for the customer" value={fmtN(d.pickups.ready_waiting)}
                                    sub={`${fmtN(d.pickups.ready_over_a_week)} have waited over a week`}
                                    tone={d.pickups.ready_over_a_week > 0 ? 'warn' : 'muted'} />
                            </InlineGrid>
                            {(d.pickups.clubs_still_to_come || []).length > 0 && (
                                <Text as="p" tone="subdued">
                                    Clubs with the most orders still to come: {d.pickups.clubs_still_to_come.slice(0, 4).map(c => `${c.club} (${c.orders})`).join(', ')}.
                                </Text>
                            )}
                        </BlockStack>
                    </Card>
                    {n?.todo?.length > 0 && (
                        <Card background="bg-surface-success">
                            <BlockStack gap="300">
                                <Text as="h3" variant="headingMd">What to do this week</Text>
                                {n.todo?.[0]?.weekly_order && (
                                    <Text as="p" tone="subdued">Everything to order this week is in the first item, one line per product.</Text>
                                )}
                                {/* One block per action with room between them (Eric, 2026-09-30): number, bold title,
                                    then the reason — one line per product when an action covers several. */}
                                <BlockStack gap="400">
                                    {n.todo.map((t, i) => {
                                        const points = (t.points && t.points.length) ? t.points : (t.detail ? [t.detail] : []);
                                        return (
                                            <div key={i} style={{ display: 'grid', gridTemplateColumns: '28px minmax(0, 1fr)', columnGap: 8 }}>
                                                <Text as="span" variant="headingSm" tone="subdued">{i + 1}.</Text>
                                                <BlockStack gap="100">
                                                    <Text as="p" variant="headingSm"><Linked text={t.title} links={t.links} /></Text>
                                                    {/* A line naming no product is indented under the product line before it (Eric, 2026-10-06). */}
                                                    {t.groups?.length
                                                        ? (t.groups.length > 1 || t.groups[0].subs.length
                                                            ? <List>{t.groups.map((g, k) => (
                                                                <List.Item key={k}>
                                                                    <Rich text={g.text} links={t.links} />
                                                                    {g.subs.length > 0 && <List>{g.subs.map((sb, m) => <List.Item key={m}><Rich text={sb} links={t.links} /></List.Item>)}</List>}
                                                                </List.Item>))}</List>
                                                            : <Text as="p"><Rich text={t.groups[0].text} links={t.links} /></Text>)
                                                        : points.length > 1
                                                            ? <List>{points.map((pt, k) => <List.Item key={k}><Rich text={pt} links={t.links} /></List.Item>)}</List>
                                                            : points.map((pt, k) => <Text as="p" key={k}><Rich text={pt} links={t.links} /></Text>)}
                                                </BlockStack>
                                            </div>
                                        );
                                    })}
                                </BlockStack>
                            </BlockStack>
                        </Card>
                    )}

                    <Box paddingBlockStart="200"><Divider /></Box>
                    <Text as="h3" variant="headingSm" tone="subdued">The details</Text>

                    <Card>
                        <BlockStack gap="300">
                            <Text as="h3" variant="headingMd">How sales are going</Text>
                            {n?.sales?.length > 0 && <List>{n.sales.map((x, i) => <List.Item key={i}>{x}</List.Item>)}</List>}
                            {/* Against the same period last year (Eric, 2026-10-05): the season moves, so the month before
                                is the wrong yardstick. */}
                            {/* Last column (Eric, 2026-10-06): the dollar difference, this period minus the same period last year. */}
                            <Table
                                headings={['Category', 'Sold, this period', 'Same period last year', 'Change', 'Sales', 'Sales last year', 'Sales vs last year']}
                                types={['text', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric']}
                                rows={(d.categories || []).slice(0, 8).map(c => [
                                    c.category, fmtN(c.units_30), fmtN(c.units_last_year),
                                    (c.units_30 < 5 && c.units_last_year < 5) ? '—' : c.units_last_year ? fmtPct(pctChange(c.units_30, c.units_last_year)) : 'new',
                                    fmtUSD(c.sales_30), fmtUSD(c.sales_last_year),
                                    <Text as="span" tone={(c.sales_30 - c.sales_last_year) < 0 ? 'critical' : 'success'}>{fmtSigned(c.sales_30 - c.sales_last_year)}</Text>])} />
                            <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
                                {[['Selling more', d.gaining], ['Selling less', d.losing]].map(([title, list]) => (
                                    <BlockStack gap="200" key={title}>
                                        <Text as="h4" variant="headingSm">{title}</Text>
                                        <Table headings={['Item', 'This period', 'Same period last year']} types={['text', 'numeric', 'numeric']}
                                            rows={(list || []).slice(0, 5).map(s => [<Item name={s.item} handle={s.b2b_handle} />, fmtN(s.units_30), fmtN(s.units_last_year ?? s.units_prior_30)])} />
                                    </BlockStack>
                                ))}
                            </InlineGrid>
                        </BlockStack>
                    </Card>

                    <Card>
                        <BlockStack gap="300">
                            <Text as="h3" variant="headingMd">You and {nearby}</Text>
                            {n?.peers?.length > 0 && <List>{n.peers.map((x, i) => <List.Item key={i}>{x}</List.Item>)}</List>}
                            <Table
                                headings={['Store', 'This period', 'vs last year', 'Last week', 'Average sale']}
                                types={['text', 'numeric', 'numeric', 'numeric', 'numeric']}
                                rows={[
                                    [<Text as="span" fontWeight="semibold">{d.town} (you)</Text>, fmtUSD(d.pulse.me.d30.sales),
                                        fmtPct(pctChange(d.pulse.me.d30.sales, d.pulse.me.ly30.sales)), fmtUSD(d.pulse.me.d7.sales),
                                        fmtUSD(d.pulse.me.d30.tx ? d.pulse.me.d30.sales / d.pulse.me.d30.tx : null)],
                                    ...((d.pulse.peers || []).length
                                        ? d.pulse.peers.map(p => [p.name, fmtUSD(p.d30.sales), fmtPct(pctChange(p.d30.sales, p.ly30.sales)), fmtUSD(p.d7.sales),
                                            fmtUSD(p.d30.tx ? p.d30.sales / p.d30.tx : null)])
                                        : [[`${Nearby}, average of ${d.pulse.peer_count}`, fmtUSD(d.pulse.peer_avg.d30.sales), fmtPct(d.trend.peer_pct_30_vs_ly),
                                            fmtUSD(d.pulse.peer_avg.d7.sales),
                                            fmtUSD(d.pulse.peer_avg.d30.tx ? d.pulse.peer_avg.d30.sales / d.pulse.peer_avg.d30.tx : null)]]),
                                ]} />
                            <Text as="h4" variant="headingSm">Selling at {nearby}, not at yours</Text>
                            <Table headings={['Item', `${Nearby} sold, this period`, 'Price', 'B2B has']} types={['text', 'numeric', 'numeric', 'numeric']}
                                rows={(d.selling_at_peers_not_here || []).slice(0, 6).map(p => [
                                    <Item name={p.item} sku={styleSku(p.item)} note={p.category} handle={p.b2b_handle} />, fmtN(p.peer_units_30), fmtUSD(p.price),
                                    // B2B's current stock (2026-09-30, Eric); briefings built before that day only know yes / no.
                                    p.b2b_available !== undefined ? fmtN(p.b2b_available) : (p.in_b2b ? 'Yes' : '0')])} />
                            {/* The opposite list (store feedback via David Thomasson, 2026-10-20): what you sell
                                that a nearby store holds unsold — who to ask for a transfer when B2B is out.
                                Briefings built before that day do not carry it. */}
                            {d.selling_here_held_nearby && (
                                <>
                                    <Text as="h4" variant="headingSm">Selling at yours, sitting at {nearby}</Text>
                                    <Text as="p" tone="subdued">
                                        Items you sold this period that a nearby store has on the shelf and sold none of. Items B2B is out of come first:
                                        ask that store for a transfer.
                                    </Text>
                                    <Table headings={['Item', 'You sold, this period', 'Your on hand', 'B2B has', `${Nearby} stock, not selling there`]}
                                        types={['text', 'numeric', 'numeric', 'numeric', 'text']}
                                        rows={(d.selling_here_held_nearby || []).slice(0, 10).map(p => [
                                            <Item name={p.item} sku={styleSku(p.item)} note={p.category} handle={p.b2b_handle} />,
                                            fmtN(p.my_units_30), fmtN(p.my_on_hand),
                                            <Text as="span" tone={p.b2b_available > 0 ? undefined : 'critical'}>{fmtN(p.b2b_available)}</Text>,
                                            <div style={{ whiteSpace: 'normal', minWidth: 180 }}>
                                                {(p.held_at || []).map(h => `${h.store} ${fmtN(h.on_hand)}`).join(', ')}
                                            </div>])} />
                                </>
                            )}
                        </BlockStack>
                    </Card>

                    <Card>
                        <BlockStack gap="300">
                            <Text as="h3" variant="headingMd">What to order from B2B</Text>
                            <Text as="p" tone="subdued">
                                The items that matter most. The order amount covers about {d.target_weeks} weeks of sales, after what you have and what is
                                already on its way to you from B2B (ordered, shipped, or delivered in the last three days). In total {fmtN(d.stock.order_items)} items need stock, about {fmtUSD(d.stock.order_cost)} at cost.
                            </Text>
                            <Table headings={['Item', 'On hand', 'On its way', 'Sold, this period', 'B2B has', 'Order']} types={['text', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric']}
                                rows={(d.order || []).map(o => [<Item name={o.item} sku={o.sku} handle={o.b2b_handle} />, fmtN(o.on_hand), fmtN(o.on_order), fmtN(o.sold_30), fmtN(o.b2b_available),
                                    <Text as="span" fontWeight="bold" tone="success">{fmtN(o.suggest)}</Text>])} />
                            {(d.already_on_its_way || []).length > 0 && (
                                <>
                                    <Text as="h4" variant="headingSm">Already on its way to you</Text>
                                    <Text as="p" tone="subdued">Running low, but your open B2B orders cover it. Nothing more to order.</Text>
                                    <Table headings={['Item', 'On hand', 'On its way', 'Sold, this period']} types={['text', 'numeric', 'numeric', 'numeric']}
                                        rows={d.already_on_its_way.map(o => [<Item name={o.item} sku={o.sku} handle={o.b2b_handle} />, fmtN(o.on_hand), fmtN(o.on_order), fmtN(o.sold_30)])} />
                                </>
                            )}
                            <Text as="h4" variant="headingSm">Selling, but B2B is out</Text>
                            <Table headings={['Item', 'On hand', 'Sold, this period']} types={['text', 'numeric', 'numeric']}
                                rows={(d.b2b_is_out || []).slice(0, 6).map(o => [<Item name={o.item} sku={o.sku} handle={o.b2b_handle} />, fmtN(o.on_hand), fmtN(o.sold_30)])} />
                            {d.stock.below_zero_items >= 10 && (
                                <Banner tone="warning">
                                    {fmtN(d.stock.below_zero_items)} items show a count below zero in Lightspeed. They were sold before the delivery was
                                    received, so receive your deliveries to make the counts and the order amounts right.
                                </Banner>
                            )}
                        </BlockStack>
                    </Card>

                    <Card>
                        <BlockStack gap="300">
                            <Text as="h3" variant="headingMd">Stock that is not selling</Text>
                            <Text as="p">
                                {n?.slow_intro || `Styles that have not sold one unit in 90 days hold ${fmtUSD(d.not_selling.cost_value)} of your stock at cost.`}
                            </Text>
                            <Table headings={['Item', 'On hand', 'Cost value', 'What to do']} types={['text', 'numeric', 'numeric', 'text']}
                                rows={(d.not_selling.top || []).map(x => [<Item name={x.item} sku={x.sku} note={x.category} handle={x.b2b_handle} />, fmtN(x.on_hand), fmtUSD(x.cost_value),
                                    <div style={{ whiteSpace: 'normal', minWidth: 160 }}>{n?.slow_actions?.[x.sku] || '—'}</div>])} />
                        </BlockStack>
                    </Card>

                </>
            )}
        </BlockStack>
    );
}
